// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import {
  BiometricAuth,
  BiometryType,
  type CheckBiometryResult,
} from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';
import { IS_NATIVE } from './platform.js';

// DECISION — biometric unlock model:
// The derived ledger key already lives in the OS Keychain/Keystore
// (secure-storage.ts). Biometric unlock is a *gate* in front of retrieving it:
// after one password login the user can re-enter with Face ID / Touch ID /
// fingerprint instead of re-deriving the key from their password. The password
// remains the fallback whenever biometry is unavailable, not enrolled, or fails
// — the "enabled" flag never removes the password path, it only adds a faster
// one. The flag itself is a device-local, non-sensitive preference and so lives
// in @capacitor/preferences (NOT the encrypted settings event log): whether this
// phone offers Face ID is a property of this phone, not of the ledger.
//
// Plugin: @aparajita/capacitor-biometric-auth is the actively-maintained
// successor to @capacitor-community/biometric-auth (the community package the
// spec named is deprecated / unmaintained for Capacitor 7+). Same capability.

const BIOMETRIC_ENABLED_KEY = 'hushold.biometricEnabled';

export interface BiometryInfo {
  available: boolean;
  type: BiometryType;
  // Human-readable name for UI copy: "Face ID", "Touch ID", "fingerprint", etc.
  friendlyName: string;
}

export function biometryFriendlyName(type: BiometryType): string {
  switch (type) {
    case BiometryType.faceId:
      return 'Face ID';
    case BiometryType.touchId:
      return 'Touch ID';
    case BiometryType.faceAuthentication:
      return 'face unlock';
    case BiometryType.fingerprintAuthentication:
      return 'fingerprint';
    case BiometryType.irisAuthentication:
      return 'iris unlock';
    default:
      return 'biometric unlock';
  }
}

// Whether this device can do biometric auth at all (hardware present + enrolled).
export async function getBiometryInfo(): Promise<BiometryInfo> {
  if (!IS_NATIVE) {
    return { available: false, type: BiometryType.none, friendlyName: 'biometric unlock' };
  }
  try {
    const info: CheckBiometryResult = await BiometricAuth.checkBiometry();
    return {
      available: info.isAvailable,
      type: info.biometryType,
      friendlyName: biometryFriendlyName(info.biometryType),
    };
  } catch {
    return { available: false, type: BiometryType.none, friendlyName: 'biometric unlock' };
  }
}

// Whether the user has opted in to biometric unlock on this device.
export async function isBiometricEnabled(): Promise<boolean> {
  if (!IS_NATIVE) return false;
  try {
    const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    return value === 'true';
  } catch {
    return false;
  }
}

export async function setBiometricEnabled(enabled: boolean): Promise<void> {
  if (!IS_NATIVE) return;
  await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: enabled ? 'true' : 'false' });
}

// Presents the native biometric prompt. Resolves true on success, false on any
// failure or cancellation — callers fall back to the password flow on false.
export async function runBiometricPrompt(reason: string): Promise<boolean> {
  if (!IS_NATIVE) return false;
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Use password',
      allowDeviceCredential: false,
      iosFallbackTitle: 'Use password',
      androidTitle: 'Unlock Hushold',
      androidSubtitle: reason,
    });
    return true;
  } catch {
    return false;
  }
}
